import { useState } from 'react';
import moment from 'moment/moment';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import { v4 } from 'uuid';
import useSequence from 'Hooks/useSequence';

const useReactionList = (reactionCollection) => {
  const reactionArray = reactionCollection?.resources()?.list() ? reactionCollection.resources().list() : [];
  const [reactions, setReactions] = useState(reactionArray);
  const {
    startSequence, hasSequenceStarted, clearSequence,
  } = useSequence();

  const reactionSummaryMap = reactions.reduce((map, reaction) => {
    // Get commonly used data from resource
    const emoji = reaction.attr('emoji');
    const isMyReaction = reaction.isTruthy('belongsToMe');

    // If an entry already exists, add owner and increment count
    if (map.has(emoji)) {
      // Otherwise create a new entry with default values
      const reactionEntry = map.get(emoji);
      reactionEntry.count++;
      reactionEntry.isSelected = reactionEntry.isSelected || isMyReaction;

      // set resource ID if this is the reaction from the user
      if (isMyReaction) {
        reactionEntry.myReactionResource = reaction;
        reactionEntry.owners.unshift('You');
      } else {
        reactionEntry.owners.push(reaction.attr('ownerName'));
      }

      return map.set(emoji, reactionEntry);
    }
    // Otherwise create a new entry with default values
    const newReactionEntry = {
      count: 1,
      owners: isMyReaction ? ['You'] : [reaction.attr('ownerName')],
      isSelected: isMyReaction,
      myReactionResource: isMyReaction ? reaction : undefined,
    };

    return map.set(emoji, newReactionEntry);
  }, new Map());


  // Convert the reaction summaries map into an array
  const reactionSummaries = Array.from(reactionSummaryMap, ([key, value]) => ({ emoji: key, ...value }));

  const refreshReactions = async () => {
    const refreshedReactions = await reactionCollection.refresh();
    const refreshedReactionArray = refreshedReactions?.resources()?.list() ? refreshedReactions?.resources()?.list() : [];
    setReactions(refreshedReactionArray);
  };

  const onFailedReactionUpdate = async (err, errorMessage = 'Failed to update reaction') => {
    await refreshReactions();
    displayErrorMessage(err, errorMessage);
  };

  const onAddReaction = async (emoji) => {
    // Get users reactions
    const isDuplicateReaction = reactions
      .filter(reaction => reaction.isTruthy('belongsToMe'))
      .filter(reaction => reaction.attr('emoji') === emoji)
      .length > 0;

    // If this is a duplicate reaction, don't add it
    if (isDuplicateReaction) {
      return;
    }
    if (hasSequenceStarted()) {
      return;
    }
    startSequence();
    // Create new emoji resource
    const newReaction = reactionCollection.create();
    const tempId = v4();
    newReaction.attr('tempId', tempId);
    newReaction.attr('emoji', emoji);
    newReaction.attr('createdAt', moment().toISOString());
    newReaction.attr('belongsToMe', 'true');

    // Optimistically add the resource to the array of reactions
    setReactions((prevState) => [...prevState, newReaction]);

    // Submit new resource and replace the temp resource with the response
    try {
      const [createdReaction] = await newReaction.submit();
      setReactions((prevState) => prevState.map((reaction) => (reaction.attr('tempId') === tempId ? createdReaction : reaction)));
    } catch (e) {
      await onFailedReactionUpdate(e, 'Failed to add reaction');
    }

    clearSequence();
  };

  const onRemoveReaction = async (reaction) => {
    if (hasSequenceStarted()) {
      return;
    }
    startSequence();
    // if there is no reaction that belongs to the user short circuit
    if (!reaction.myReactionResource) {
      return;
    }

    // Remove the reaction optimistically
    setReactions((prevState) => prevState.filter(resource => resource.id() !== reaction.myReactionResource.id()));
    try {
      await reaction.myReactionResource.delete();
    } catch (e) {
      await onFailedReactionUpdate(e, 'Failed to remove reaction');
    }

    clearSequence();
  };


  return {
    reactions,
    reactionSummaries,
    onAddReaction,
    onRemoveReaction,
  };
};

export { useReactionList };
